import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import QRCode from "qrcode";
import { JOIN_PATH, SPECS, specsPulse } from "./specsTheme";

const QRJoinCard = ({ joinUrl }) => {
  const [dataUrl, setDataUrl] = useState(null);
  const [error, setError] = useState(false);

  const url = useMemo(() => {
    if (joinUrl) return joinUrl;
    if (typeof window !== "undefined") {
      return `${window.location.origin}${JOIN_PATH}`;
    }
    return JOIN_PATH;
  }, [joinUrl]);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    QRCode.toDataURL(url, {
      width: 420,
      margin: 2,
      errorCorrectionLevel: "H",
      color: { dark: "#0a0a0a", light: "#ffffff" },
    })
      .then((data) => {
        if (!cancelled) setDataUrl(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 3,
        flex: "0 0 auto",
        alignSelf: "center",
        width: "min(100%, 380px)",
        height: "auto",
        maxWidth: "100%",
        maxHeight: "100%",
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "clamp(10px, 1.4vh, 16px)",
        p: "clamp(10px, 1.8vh, 22px)",
        borderRadius: "clamp(18px, 3vh, 28px)",
        overflow: "hidden",
        boxSizing: "border-box",
        containerType: "inline-size",
        background:
          "linear-gradient(160deg, rgba(18,8,14,0.94) 0%, rgba(8,4,6,0.9) 100%)",
        border: `2px solid ${SPECS.orange}`,
        boxShadow: `
          0 0 0 1px rgba(255,45,138,0.3),
          0 0 40px rgba(255,45,138,0.28),
          0 0 80px rgba(255,193,7,0.12),
          inset 0 1px 0 rgba(255,255,255,0.08)
        `,
        backdropFilter: "blur(16px)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: -2,
          borderRadius: "30px",
          pointerEvents: "none",
          background: `linear-gradient(135deg, ${SPECS.magenta}55, transparent 40%, transparent 60%, ${SPECS.gold}55)`,
          opacity: 0.55,
          animation: `${specsPulse} 4s ease-in-out infinite`,
          zIndex: -1,
          filter: "blur(10px)",
        }}
      />

      <Typography
        sx={{
          fontFamily: SPECS.fontDisplay,
          fontWeight: 800,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          textAlign: "center",
          fontSize: "clamp(0.72rem, 7.5cqi, 1.25rem)",
          flexShrink: 0,
          background: SPECS.textGradient,
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          color: "transparent",
          filter: "drop-shadow(0 0 12px rgba(255,45,138,0.45))",
        }}
      >
        Scan to Join MYM
      </Typography>

      <Box
        sx={{
          flex: "0 0 auto",
          width: "min(156px, 42vw, 20vh)",
          "@media (min-width: 900px)": {
            width: "min(250px, 100%, 32vh)",
          },
          aspectRatio: "1",
          p: "clamp(6px, 1vh, 12px)",
          borderRadius: "18px",
          bgcolor: "#fff",
          boxShadow: `0 0 28px rgba(255,107,44,0.35)`,
          lineHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {dataUrl ? (
          <Box
            component="img"
            src={dataUrl}
            alt="Scan to join Mind Your Music"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : error ? (
          <Typography
            sx={{
              color: "#333",
              fontSize: "0.75rem",
              textAlign: "center",
              px: 2,
              fontFamily: SPECS.fontBody,
            }}
          >
            {url}
          </Typography>
        ) : (
          <CircularProgress size={36} sx={{ color: SPECS.orange }} />
        )}
      </Box>

      <Typography
        sx={{
          fontFamily: SPECS.fontDisplay,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          textAlign: "center",
          fontSize: "clamp(0.55rem, 5.4cqi, 0.8rem)",
          maxWidth: "100%",
          flexShrink: 0,
          color: SPECS.gold,
          textShadow: `0 0 12px ${SPECS.gold}66`,
        }}
      >
        Play • Compete • Enjoy
      </Typography>
    </Box>
  );
};

export default QRJoinCard;
